import type { EmblaCarouselType } from "embla-carousel";
import type { AutoplayType } from "embla-carousel-autoplay";
import { useCallback, useEffect, useState } from "react";

export type EmblaState = {
  slideCount: number;
  currentIndex: number;
  hasPrev: boolean;
  hasNext: boolean;
  goTo: (target: "next" | "previous" | number) => void;
};

// Accept either a ref or a direct instance
type AutoplayRefOrInstance =
  | AutoplayType
  | { current: AutoplayType }
  | undefined;

export function useEmblaState(
  emblaApi: EmblaCarouselType | undefined,
  autoplay?: AutoplayRefOrInstance
): EmblaState {
  const [state, setState] = useState<Omit<EmblaState, "goTo">>({
    slideCount: 0,
    currentIndex: 0,
    hasPrev: false,
    hasNext: false
  });

  // Update carousel state when selection changes or carousel re-inits
  useEffect(() => {
    if (!emblaApi) return;

    const updateState = () => {
      setState({
        slideCount: emblaApi.slideNodes().length,
        currentIndex: emblaApi.selectedScrollSnap(),
        hasPrev: emblaApi.canScrollPrev(),
        hasNext: emblaApi.canScrollNext()
      });
    };

    updateState();

    emblaApi.on("select", updateState);
    emblaApi.on("reInit", updateState);

    return () => {
      emblaApi.off("select", updateState);
      emblaApi.off("reInit", updateState);
    };
  }, [emblaApi]);

  const resetAutoplay = useCallback(() => {
    if (!autoplay) return;
    if ("current" in autoplay && autoplay.current) {
      autoplay.current.reset();
    } else if ("reset" in autoplay) {
      autoplay.reset();
    }
  }, [autoplay]);

  const goTo = useCallback(
    (target: "next" | "previous" | number) => {
      if (!emblaApi) return;

      if (target === "next") emblaApi.scrollNext();
      else if (target === "previous") emblaApi.scrollPrev();
      else if (typeof target === "number") emblaApi.scrollTo(target);

      resetAutoplay();
    },
    [emblaApi, resetAutoplay]
  );

  return { ...state, goTo };
}
